/**
 * W-070 — Support messages. Stories: US-911 (contact), US-912 (suggestions).
 *
 * One inbox for both kinds, with status and filters, and the detail in an
 * expandable row rather than a second route — the messages are short and the
 * operator works through them in sequence.
 */
import { List, useTable } from '@refinedev/antd'
import { useUpdate } from '@refinedev/core'
import { Descriptions, Select, Space, Table, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import { ModelInsights } from '@/components'
import { useAdminTranslate } from '@/providers/i18n-provider'
import type { SupportKind, SupportMessage } from '@/types'

interface SupportRow extends SupportMessage {
  profiles?: { full_name: string; phone: string } | null
}

const KINDS = ['contact', 'suggestion'] as const satisfies readonly SupportKind[]
const STATUSES = ['open', 'in_progress', 'closed'] as const

export const SupportMessageListPage = () => {
  const translate = useAdminTranslate()
  const kindLabel = (value: SupportKind) =>
    translate(`admin.screens.support.kind${value === 'contact' ? 'Contact' : 'Suggestion'}`)
  const statusLabel = (value: string) => translate(`admin.status.support.${value}`)
  const [kind, setKind] = useState<SupportKind | undefined>()
  const [status, setStatus] = useState<string | undefined>('open')
  const { mutate: update, isPending } = useUpdate()

  const { tableProps } = useTable<SupportRow>({
    resource: 'support_messages',
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    pagination: { pageSize: 25 },
    meta: { select: '*, profiles(full_name, phone)' },
    filters: {
      permanent: [
        ...(kind ? [{ field: 'kind', operator: 'eq' as const, value: kind }] : []),
        ...(status ? [{ field: 'status', operator: 'eq' as const, value: status }] : []),
      ],
    },
  })
  const resultCount =
    (typeof tableProps.pagination === 'object' ? tableProps.pagination.total : undefined) ??
    (tableProps.dataSource ?? []).length

  return (
    <List
      title={translate('admin.pages.supportMessages')}
      canCreate={false}
      headerProps={{
        className: 'admin-list-header',
        subTitle: translate('admin.screens.support.subtitle'),
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <ModelInsights model="support" />
        <div className="admin-filter-bar">
          <Space wrap>
            <Select
              allowClear
              placeholder={translate('admin.common.type')}
              style={{ width: 180 }}
              value={kind}
              onChange={value => setKind(value)}
              options={KINDS.map(value => ({ value, label: kindLabel(value) }))}
            />
            <Select
              allowClear
              placeholder={translate('admin.columns.status')}
              style={{ width: 180 }}
              value={status}
              onChange={value => setStatus(value)}
              options={STATUSES.map(value => ({ value, label: statusLabel(value) }))}
            />
          </Space>
        </div>

        <div className="admin-filter-summary">
          <Typography.Text type="secondary">
            {status
              ? translate('admin.screens.support.queue', { status: statusLabel(status) })
              : translate('admin.screens.support.allRequests')}
          </Typography.Text>
          <Typography.Text type="secondary">
            {translate('admin.screens.support.messageCount', { count: resultCount })}
          </Typography.Text>
        </div>

        <Table<SupportRow>
          {...tableProps}
          rowKey="id"
          scroll={{ x: 860 }}
          sticky
          expandable={{
            expandedRowRender: record => (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Paragraph>{record.body}</Typography.Paragraph>
                <Descriptions size="small" column={3}>
                  <Descriptions.Item label={translate('admin.screens.support.appVersion')}>
                    {record.app_version ?? '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('admin.screens.support.platform')}>
                    {record.platform ?? '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('admin.columns.phone')}>
                    {record.profiles?.phone ?? '—'}
                  </Descriptions.Item>
                </Descriptions>
              </Space>
            ),
          }}
          columns={[
            {
              title: translate('admin.screens.support.receivedAt'),
              dataIndex: 'created_at',
              render: (value: string) => dayjs(value).format('DD/MM/YYYY HH:mm'),
            },
            {
              title: translate('admin.common.type'),
              dataIndex: 'kind',
              render: (value: SupportKind) => <Tag>{kindLabel(value)}</Tag>,
            },
            {
              title: translate('admin.screens.support.author'),
              dataIndex: ['profiles', 'full_name'],
              render: (value: string | undefined) =>
                value ?? translate('admin.screens.support.deletedAccount'),
            },
            {
              title: translate('admin.screens.support.subject'),
              dataIndex: 'subject',
              render: (value: string | null) => value ?? '—',
            },
            {
              title: translate('admin.columns.status'),
              dataIndex: 'status',
              render: (value: string, record: SupportRow) => (
                <Select
                  size="small"
                  style={{ width: 140 }}
                  value={value}
                  loading={isPending}
                  disabled={isPending}
                  options={STATUSES.map(option => ({ value: option, label: statusLabel(option) }))}
                  onChange={next =>
                    update({
                      resource: 'support_messages',
                      id: record.id,
                      values: { status: next },
                    })
                  }
                />
              ),
            },
          ]}
        />
      </Space>
    </List>
  )
}
